/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address/src/modules/checkout-shipping-address/../../common/components/address-add';
import { IAddressSelectItem, IAddressSelectProps } from '@msdyn365-commerce-modules/address/src/modules/checkout-shipping-address/../../common/components/address-select';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address/src/modules/checkout-shipping-address/../../common/components/address-show';
import { ICheckoutShippingAddressViewProps } from '@msdyn365-commerce-modules/address/src/modules/checkout-shipping-address/./checkout-shipping-address';
import {IAddressResource} from "@msdyn365-commerce-modules/address/src/common";

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({
    AddressDetail,
    items
}) => {

    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem) => {
                return (
                    <>
                        {item.description}
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Address Select Component.
 * @param param0 - Root param.
 * @param param0.SelectAddress - Select address.
 * @param param0.addButton - Add button.
 * @param param0.items - IAddressSelectItem[].
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - SelectAddress Node.
 */
const AddressSelect: React.FC<IAddressSelectProps> = ({
    SelectAddress,
    addButton,
    items,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {

    return (
        <Node {...SelectAddress}>
            {addButton}
            {items.map((item: IAddressSelectItem) => {
                const SelectItem = item.SelectItem;
                return (<Node {...SelectItem} key={item.key}>
                    {item.input}
                    <AddressShow {...item.showItems} />
                </Node>);
            })}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */

const checkPhone = (addressPhoneRequiredErrorMessage: string) => {
    const saveAddressButton = document.querySelector(
        ".ms-checkout__guided-card-btn-save.msc-btn"
    ) as HTMLButtonElement;
    const phone = document.getElementById(
        "shipping_addressphone"
    ) as HTMLInputElement;
    if (phone) {
        const phoneCtn = phone.closest(".msc-address-form__item") as HTMLElement;
        const phoneAlert = phoneCtn.getElementsByClassName(
            "msc-address-form__alert"
        )[0];

        phone.setAttribute("aria-required", "true");
        phone.setAttribute("type", "tel");
        phone.setAttribute("maxlength", "15");
        if (phone.value.length === 0) {
            saveAddressButton.setAttribute("disabled", "true");
        }
        phone.onkeyup = () => {
            if (phone.value.length < 1) {
                phoneCtn.classList.add("address-form__item-invalid");
                phoneAlert.textContent = addressPhoneRequiredErrorMessage;
                saveAddressButton.setAttribute("disabled", "true");
            } else {
                phoneAlert.textContent === addressPhoneRequiredErrorMessage && phoneCtn.classList.contains('address-form__item-invalid') && phoneCtn.classList.remove("address-form__item-invalid");
                if(phoneAlert.textContent === addressPhoneRequiredErrorMessage) {
                    phoneAlert.textContent = "";
                }
                if(document.getElementsByClassName('address-form__item-invalid').length > 0) {
                    saveAddressButton.setAttribute("disabled", "true");
                } else {
                    isRequiredIsFilled();
                }
            }
        };
        document.getElementsByClassName("msc-address-form")[0].querySelectorAll("input, select").forEach(function(element) {
            (element as HTMLInputElement).onchange = () => {
                isRequiredIsFilled();
            }
        })
        const isRequiredIsFilled = () => {
            let allAreFilled = true;
            document.getElementsByClassName("msc-address-form")[0].querySelectorAll("[aria-required = 'true']").forEach(function(element) {
                if (!allAreFilled) return;
                if (!(element as HTMLInputElement).value) { allAreFilled = false;  return; }
            })
            if (allAreFilled && document.getElementsByClassName('address-form__item-invalid').length === 0) {
                saveAddressButton.removeAttribute("disabled");
            } else {
                saveAddressButton.setAttribute("disabled", "true");
            }
        }

    }
};
interface IAddressAddUpdatePropsExt extends IAddressAddUpdateProps {
    resources:IAddressResource;
}
const AddressAddUpdate: React.FC<IAddressAddUpdatePropsExt> = (props) => {
    const {
        AddressForm,
            heading,
            items,
            hasError,
            error,
            isShowSaveButton,
            saveButton,
            isShowCancelButton,
            cancelButton
    } = props;
    // Phone Verifications
    React.useEffect(() => {
        checkPhone(props.resources.addressPhoneRequiredErrorMessage);
    }, [
        (document?.getElementById("shipping_addressphone") as HTMLInputElement)
            ?.value
    ]);
    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                return (<Node {...AddressItem} key={key}>
                    {label}
                    {alert}
                    {input}
                </Node>);
            })}
            {_isMilitaryCheck}
            {hasError && <Node {...error.AddressError}>
                {error.title}
                {error.message}
            </Node>}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const _isMilitaryCheck = () => {
    const [isMilitary, setIsMilitary] = React.useState(false);
    const handleChange = () => {
        setIsMilitary(!isMilitary);
    }
    return (
        <label>
            <input type={'checkbox'} onChange={handleChange}/>
            Is this a PO box or a Military address
        </label>
    );
}
/**
 * Checkout Shipping Address View Component.
 * @param props - Props.
 * @returns - CheckoutShippingAddress Module.
 */
const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewProps> = props => {
    const { CheckoutShippingAddress, viewState, showAddress, showAddressSelect, showAddOrUpdateAddress, cartLineImages } = props;
    return (
        <Module {...CheckoutShippingAddress}>
            {cartLineImages}
            {viewState.isShowAddress && <AddressShow {...showAddress} />}
            {viewState.isShowAddresList && <AddressSelect {...showAddressSelect} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate resources={props.resources} {...showAddOrUpdateAddress} />}
        </Module>
    );
};

export default CheckoutShippingAddressView;
